import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Shield, Share2, Pencil, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/ui/PageTransition';
// import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';

const Preview = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profileData, setProfileData] = useState<any>(null);
  const [personalData, setPersonalData] = useState<any>(null);
  const [educationData, setEducationData] = useState<any[]>([]);
  const [workData, setWorkData] = useState<any[]>([]);
  const [privacySettings, setPrivacySettings] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  // const { data: session, isLoading: sessionLoading } = useQuery({
  //   queryKey: ['session'],
  //   queryFn: async () => {
  //     // const { data } = await supabase.auth.getSession(); // Needs Firebase equivalent
  //     // return data.session;
  //     return null; // Placeholder
  //   }
  // });
  const session = null; // Placeholder
  const sessionLoading = true; // Placeholder

  useEffect(() => {
    // const fetchProfileData = async () => {
    //   if (!session?.user) return;
      
    //   setLoading(true);
    //   try {
    //     // Get profile data - Needs to be refactored to use Azure PostgreSQL services
    //     // const { data: profileData, error: profileError } = await supabase
    //     //   .from('profiles')
    //     //   .select('*')
    //     //   .eq('user_id', session.user.id)
    //     //   .maybeSingle();
          
    //     // if (profileError) throw profileError;
        
    //     // if (!profileData) {
    //     //   toast({
    //     //     title: "No profile found",
    //     //     description: "Please complete your profile first",
    //     //     variant: "destructive",
    //     //   });
    //     //   navigate('/profile');
    //     //   return;
    //     // }
        
    //     // setProfileData(profileData);
        
    //     // Get personal data - Needs refactor
    //     // Get education data - Needs refactor
    //     // Get work data - Needs refactor
    //     // Get privacy settings - Needs refactor
    //     // Get profile image - Needs refactor (Firebase Storage or Azure)

    //   } catch (error: any) {
    //     console.error('Error fetching profile data:', error);
    //     toast({
    //       title: "Error loading profile",
    //       description: error.message,
    //       variant: "destructive",
    //     });
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    
    // if (session) {
    //   fetchProfileData();
    // }
    setLoading(false); // Temporarily set loading to false
  }, [session, navigate, toast]);

  const isFieldVisible = (field: string): boolean => {
    return privacySettings[field] ?? true;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return '';
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  };

  if (sessionLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sessionLoading && !session) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow pt-28 pb-20">
          <div className="container px-6 mx-auto">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                  <div>
                    <div className="flex items-center mb-2">
                      <Eye className="h-5 w-5 text-primary mr-2" />
                      <h1 className="text-3xl font-bold">Preview Mode</h1>
                    </div>
                    <p className="text-muted-foreground">This is how others will see your profile</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
                    <Button asChild variant="outline" size="sm" className="rounded-full">
                      <a href="/profile">
                        <Pencil className="mr-1 h-4 w-4" />
                        Edit Profile
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => navigate('/profile')}
                    >
                      <Shield className="mr-1 h-4 w-4" />
                      Adjust Privacy
                    </Button>
                  </div>
                </div>
                
                <div className="bg-secondary/30 rounded-xl p-4 mb-8 flex items-center">
                  <Eye className="h-5 w-5 text-primary mr-2" />
                  <p className="text-sm">You're viewing your profile in preview mode with your current privacy settings applied.</p>
                </div>
                
                {/* Profile Preview */}
                <Card className="overflow-hidden">
                  {/* Profile Header */}
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-muted flex items-center justify-center border-4 border-white overflow-hidden">
                        {profileImage ? (
                          <img 
                            src={profileImage} 
                            alt={profileData?.full_name || 'Profile'} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl text-muted-foreground">
                            {profileData?.full_name ? profileData.full_name.charAt(0) : '?'}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        {isFieldVisible('full_name') && profileData?.full_name ? (
                          <h2 className="text-2xl font-bold mb-1">{profileData.full_name}</h2>
                        ) : (
                          <h2 className="text-2xl font-bold mb-1">
                            <span className="bg-muted/50 text-transparent rounded">Anonymous User</span>
                          </h2>
                        )}
                        
                        <div className="space-y-1">
                          {isFieldVisible('date_of_birth') && profileData?.date_of_birth ? (
                            <p className="text-muted-foreground">
                              {calculateAge(profileData.date_of_birth)} years old
                              {isFieldVisible('gender') && profileData?.gender && `, ${profileData.gender}`}
                            </p>
                          ) : (
                            isFieldVisible('gender') && profileData?.gender && (
                              <p className="text-muted-foreground">{profileData.gender}</p>
                            )
                          )}
                          
                          {isFieldVisible('religion') && profileData?.religion && (
                            <p className="text-muted-foreground">{profileData.religion}</p>
                          )}
                        </div>
                        
                        <div className="mt-4 flex flex-wrap gap-2">
                          {workData && workData.length > 0 && isFieldVisible('work') ? (
                            <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                              {workData[0].position}
                            </span>
                          ) : null}
                          
                          {educationData && educationData.length > 0 && isFieldVisible('education') ? (
                            <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                              {educationData[0].degree || educationData[0].level}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Personal Information */}
                  <div className="p-6 border-b border-muted">
                    <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                      {isFieldVisible('full_name') && profileData?.full_name && (
                        <div>
                          <p className="text-sm text-muted-foreground">Name</p>
                          <p className="font-medium">{profileData.full_name}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('date_of_birth') && profileData?.date_of_birth && (
                        <div>
                          <p className="text-sm text-muted-foreground">Age</p>
                          <p className="font-medium">{calculateAge(profileData.date_of_birth)} years</p>
                        </div>
                      )}
                      
                      {isFieldVisible('gender') && profileData?.gender && (
                        <div>
                          <p className="text-sm text-muted-foreground">Gender</p>
                          <p className="font-medium">{profileData.gender}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('religion') && profileData?.religion && (
                        <div>
                          <p className="text-sm text-muted-foreground">Religion</p>
                          <p className="font-medium">{profileData.religion}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('marital_status') && profileData?.marital_status && (
                        <div>
                          <p className="text-sm text-muted-foreground">Marital Status</p>
                          <p className="font-medium">{profileData.marital_status}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('mother_tongue') && personalData?.mother_tongue && (
                        <div>
                          <p className="text-sm text-muted-foreground">Mother Tongue</p>
                          <p className="font-medium">{personalData.mother_tongue}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('height') && personalData?.height && (
                        <div>
                          <p className="text-sm text-muted-foreground">Height</p>
                          <p className="font-medium">{personalData.height}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('weight') && personalData?.weight && (
                        <div>
                          <p className="text-sm text-muted-foreground">Weight</p>
                          <p className="font-medium">{personalData.weight}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('blood_group') && personalData?.blood_group && (
                        <div>
                          <p className="text-sm text-muted-foreground">Blood Group</p>
                          <p className="font-medium">{personalData.blood_group}</p>
                        </div>
                      )}
                      
                      {isFieldVisible('nationality') && personalData?.nationality && (
                        <div>
                          <p className="text-sm text-muted-foreground">Nationality</p>
                          <p className="font-medium">{personalData.nationality}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Contact Information */}
                  {(isFieldVisible('email') || isFieldVisible('phone') || isFieldVisible('address') || 
                    isFieldVisible('city') || isFieldVisible('state') || isFieldVisible('country')) ? (
                    <div className="p-6 border-b border-muted">
                      <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                        {isFieldVisible('phone') && profileData?.phone_number && (
                          <div>
                            <p className="text-sm text-muted-foreground">Phone</p>
                            <p className="font-medium">{profileData.phone_number}</p>
                          </div>
                        )}
                        
                        {isFieldVisible('email') && profileData?.email && (
                          <div>
                            <p className="text-sm text-muted-foreground">Email</p>
                            <p className="font-medium">{profileData.email}</p>
                          </div>
                        )}
                        
                        {isFieldVisible('city') && profileData?.city && (
                          <div>
                            <p className="text-sm text-muted-foreground">City</p>
                            <p className="font-medium">{profileData.city}</p>
                          </div>
                        )}
                        
                        {isFieldVisible('state') && profileData?.state && (
                          <div>
                            <p className="text-sm text-muted-foreground">State</p>
                            <p className="font-medium">{profileData.state}</p>
                          </div>
                        )}
                        
                        {isFieldVisible('country') && profileData?.country && (
                          <div>
                            <p className="text-sm text-muted-foreground">Country</p>
                            <p className="font-medium">{profileData.country}</p>
                          </div>
                        )}
                        
                        {isFieldVisible('address') && profileData?.address && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-muted-foreground">Address</p>
                            <p className="font-medium">{profileData.address}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 border-b border-muted">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Contact Information</h3>
                        <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full flex items-center">
                          <Shield className="h-3 w-3 mr-1" />
                          Private
                        </span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center">
                        <div className="text-center">
                          <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Contact information is private</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Viewers need to request access to see this information
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Education */}
                  {isFieldVisible('education') && educationData.length > 0 ? (
                    <div className="p-6 border-b border-muted">
                      <h3 className="text-lg font-semibold mb-4">Education</h3>
                      <div className="space-y-4">
                        {educationData.map((edu, index) => (
                          <div key={index} className="bg-muted/20 p-4 rounded-lg">
                            <p className="font-medium">
                              {edu.degree ? edu.degree : edu.level} 
                              {edu.specialization ? ` in ${edu.specialization}` : ''}
                            </p>
                            <p className="text-sm text-muted-foreground">{edu.institution}</p>
                            {(edu.year_start || edu.year_end) && (
                              <p className="text-sm text-muted-foreground">
                                {edu.year_start || '?'} - {edu.year_end || 'Present'}
                              </p>
                            )}
                            {edu.grade && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Grade/GPA: {edu.grade}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (!isFieldVisible('education') && educationData.length > 0) ? (
                    <div className="p-6 border-b border-muted">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Education</h3>
                        <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full flex items-center">
                          <Shield className="h-3 w-3 mr-1" />
                          Private
                        </span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center">
                        <div className="text-center">
                          <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Education information is private</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Viewers need to request access to see this information
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Career */}
                  {isFieldVisible('work') && workData.length > 0 ? (
                    <div className="p-6 border-b border-muted">
                      <h3 className="text-lg font-semibold mb-4">Career</h3>
                      <div className="space-y-4">
                        {workData.map((work, index) => (
                          <div key={index} className="bg-muted/20 p-4 rounded-lg">
                            <p className="font-medium">{work.position}</p>
                            <p className="text-sm text-muted-foreground">{work.company}</p>
                            {(work.start_date || work.is_current) && (
                              <p className="text-sm text-muted-foreground">
                                {work.start_date ? formatDate(work.start_date) : '?'} - {
                                  work.is_current ? 'Present' : (work.end_date ? formatDate(work.end_date) : '?')
                                }
                              </p>
                            )}
                            {work.location && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Location: {work.location}
                              </p>
                            )}
                            {work.employment_type && (
                              <p className="text-sm text-muted-foreground">
                                Type: {work.employment_type}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (!isFieldVisible('work') && workData.length > 0) ? (
                    <div className="p-6 border-b border-muted">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Career</h3>
                        <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full flex items-center">
                          <Shield className="h-3 w-3 mr-1" />
                          Private
                        </span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center">
                        <div className="text-center">
                          <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Career information is private</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Viewers need to request access to see this information
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Family Information */}
                  {!isFieldVisible('family') ? (
                    <div className="p-6 border-b border-muted">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Family Information</h3>
                        <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full flex items-center">
                          <Shield className="h-3 w-3 mr-1" />
                          Private
                        </span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center">
                        <div className="text-center">
                          <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Family information is private</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Viewers need to request access to see this information
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </Card>
                
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="rounded-full"
                    onClick={() => navigate('/profile')}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => navigate('/dashboard')}
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    Share Profile
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default Preview;
